'use strict';

import { ELEMENT_STATE } from '@/constants/element/constants';
import EcsElement from '@/components/ecs-element/EcsElement';
import { MEDIA_TYPE_TELEPHONY } from '@/constants/socket-events';

const socketEventActions = {
  SOCKET_EVENT_ELEMENT_STATE_CHANGED_EVENT({ dispatch, getters, commit, rootGetters }, event) {
    const element = new EcsElement(event);

    if (element.connectedPartiesListNew) {
      element.connectedPartiesList = structuredClone(element.connectedPartiesListNew);
    }

    // NOTE: Patch to manage a declined consult
    // We need to remove the consult.receiver data from the interaction
    if (event.newElementStateId === ELEMENT_STATE.abandoned && event.oldElementStateId === ELEMENT_STATE.activating) {
      const activeInteraction = rootGetters['INTERACTIONS/getActiveInteraction'](element.interactionId);
      if (activeInteraction && activeInteraction.isConsultRequestor) {
        activeInteraction.consult.receiver = null;
        commit('INTERACTIONS/updateActiveInteraction', activeInteraction, { root: true });
      }
    }
    // ^-- End Patch --^

    const existingElement = getters.getElement(event.elementId);
    const eventMediaType = event.interactionMediaType.toUpperCase();
    const stateCasesToRemoveElement = [
      ELEMENT_STATE.busy,
      ELEMENT_STATE.forwarded,
      ELEMENT_STATE.canceled,
      ELEMENT_STATE.cpaTimeout,
      ELEMENT_STATE.serviceUnavailable
    ];

    // Store answeredAt
    if (
      event.newElementStateId === ELEMENT_STATE.active &&
      (!existingElement || event.oldElementStateId === ELEMENT_STATE.activating)
    ) {
      element.answeredAt = new Date().toISOString();
      commit('setElementAnsweredAt', { elementId: element.elementId, answeredAt: element.answeredAt });
    }

    if (!element.answeredAt) {
      element.answeredAt = getters.getElementAnsweredAt(element.elementId);
    }

    if (existingElement) {
      if (stateCasesToRemoveElement.includes(event.newElementStateId)) {
        dispatch('removeElement', element);
      } else if (event.newElementStateId === ELEMENT_STATE.abandoned && eventMediaType === MEDIA_TYPE_TELEPHONY) {
        const elements = getters.getElementsForInteraction(event.interactionId);
        dispatch(elements.length > 1 ? 'removeElement' : 'editElement', element);
      } else {
        dispatch('editElement', element);
      }
    } else {
      // AWC-941: Receptor patch - Remove when AIR-6908 is resolved
      if (['CallBack', 'CallBack Telephony', 'UndertakeHandling'].includes(event.handlingType)) {
        const receptor = {
          type: 'customer-type-id-should-go-here',
          id: event.contactId,
          name: event.customerFullName || event.destination
        };
        element.receptor = receptor;
      }
      // ^-- End Patch --^
      dispatch('createElement', element);
    }
  }
};

const stateActions = {
  createElement: ({ commit, getters, rootGetters }, element) => {
    const interaction = rootGetters['INTERACTIONS/getInteraction'](element.interactionId);
    if (!interaction) {
      commit('createElement', new EcsElement(element));
      return;
    }

    const existingElement = getters.getElement(element.elementId);
    if (existingElement) {
      return;
    }

    if (interaction.isTelephony && !element.recording) {
      const recordingProfile = rootGetters['AGENT/loggedInAgent'].callRecording;
      const recordingState =
        rootGetters['INTERACTIONS/recordings/getCallRecordingStateByInteractionId'](interaction.interactionId) || {};

      if (recordingProfile) {
        element.recording = {
          isEnabled: recordingProfile.recordingOption.toUpperCase().includes('ALWAYS') || recordingState.isRecording,
          isPaused: recordingState.isPaused
        };
      } else {
        element.recording = {
          isEnabled: false,
          isPaused: false
        };
      }
    }

    commit('createElement', new EcsElement(element));
  },

  addElement: ({ dispatch, getters }, element) => {
    const existingElement = getters.getElement(element.elementId);
    if (existingElement) {
      dispatch('editElement', element);
    } else {
      dispatch('createElement', element);
    }
  },

  editElement: ({ commit, getters }, element) => {
    const existingElement = getters.getElement(element.elementId);
    if (!existingElement) {
      return;
    }

    element.answeredAt = element.answeredAt || existingElement.answeredAt;
    element.receptor = element.receptor || existingElement.receptor;
    element.recording = element.recording || existingElement.recording;
    commit('updateElement', element);
  },

  removeElement: ({ commit, getters }, { elementId }) => {
    const existingElement = getters.getElement(elementId);

    if (!existingElement) {
      return;
    }

    commit('deleteElement', elementId);
    commit('unsetElementAnsweredAt', elementId);
    commit('CONVERSATIONS/deleteConversation', elementId, { root: true });
  },

  removeHistoricalElementsForInteraction: ({ commit, getters }, { interactionId }) => {
    commit('deleteHistoricalElementsForInteraction', interactionId);
  },

  removeClosedElementsForInteraction: ({ commit, getters }, { interactionId }) => {
    commit('deleteHistoricalElementsForInteraction', interactionId);
  },

  setElementContactInformationForEmail: ({ commit, getters }, payload) => {
    const existingElement = getters.getElement(payload.elementId);
    if (!existingElement) {
      return;
    }

    commit('setElementContactInformationForEmail', payload);
  },

  clearInactiveElementsAnsweredAt: ({ commit, getters }, elementIds) => {
    commit('clearInactiveElementsAnsweredAt', elementIds);
  }
};

export default {
  ...socketEventActions,
  ...stateActions
};
